import { companyRequest } from '@/company/api';
import type { ScopedConnectionItem } from './connection-api';
import type { NativePostgresSetupState } from './native-postgres-setup/model';

type CatalogueEntryBase = {
  slug: string;
  name: string;
  description: string;
  img_src?: string | null;
  categories?: string[];
};

export type PipedreamApp = CatalogueEntryBase & {
  provider?: 'pipedream';
  enabled?: boolean;
  hasActions?: boolean | null;
  authType?: string | null;
  /** The app's reviewed reads, when it has any (connect-time access). */
  readCatalogue?: import('./connect-access/model').ReadCatalogue;
};
export type PostgresEntry = CatalogueEntryBase & {
  provider: 'postgres';
  setup: NativePostgresSetupState;
};
export type ShopifyDevAppEntry = CatalogueEntryBase & {
  provider: 'shopify_dev_app';
  setup: { available: boolean; canConnectCompany: boolean };
  connected: boolean;
};
export type CustomerAppEntry = CatalogueEntryBase & {
  provider: 'customer_app';
  item: ScopedConnectionItem;
};
/** One entry of the Connections catalogue; Poneva-run integrations and provider apps alike. */
export type CatalogueEntry = PipedreamApp | PostgresEntry | ShopifyDevAppEntry | CustomerAppEntry;

/** GET /api/v1/company/connections/catalogue: one list, Poneva-run entries first. */
export type CatalogueResponse = {
  configured: boolean;
  /** The provider's search failed; Poneva-run entries are still listed. */
  unavailable?: boolean;
  canManage?: boolean;
  apps: CatalogueEntry[];
  nextCursor: string | null;
  totalCount?: number | null;
};

export const isPipedreamApp = (entry: CatalogueEntry): entry is PipedreamApp =>
  !entry.provider || entry.provider === 'pipedream';

export type ScopedConnectionPresentation = ScopedConnectionItem & { imageUrl?: string | null };

export function itemFor(
  app: PipedreamApp,
  audience: 'private' | 'company',
): ScopedConnectionPresentation {
  return {
    id: `pipedream:${app.slug}`,
    name: app.name,
    description: app.description,
    audience,
    status: 'not_connected',
    connectionId: null,
    accountLabel: null,
    connectedAt: null,
    lastUsedAt: null,
    actions: { connect: { available: true }, disconnect: { available: false } },
    imageUrl: app.img_src ?? null,
    ...(app.readCatalogue ? { readCatalogue: app.readCatalogue } : {}),
  };
}

export function readCatalogue(query: string, after?: string, apps?: string) {
  const params = new URLSearchParams({ q: query });
  if (after) params.set('after', after);
  if (apps) {
    params.delete('q');
    params.set('apps', apps);
  }
  return companyRequest<CatalogueResponse>(`/api/v1/company/connections/catalogue?${params}`);
}

export function uniqueApps(apps: CatalogueEntry[]) {
  return [
    ...new Map(
      apps
        .filter((app) => app.slug && app.name)
        .map((app) => [`${app.provider ?? 'pipedream'}:${app.slug}`, app]),
    ).values(),
  ];
}
